import React, { useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ProgressBar from "../../../../UI/ProgressBar";
import { useGetAdminCompanyWiseBreakdownQuery } from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { Error404 } from "../../../../UI/404/Error404";
import { SortIcon } from "../../../../Super Admin/Manage Organization/Employee/EmployeeTable";
import { ReportsTruncatedName } from "../../../../Super Admin/Super Admin Reports/ReportsTruncatedName";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectSelectedCompany,
} from "../../../../../redux-toolkit/features/auth/authSlice";
import { resolveActiveCompanyId } from "../../../../../utils/companyContextNavigation";

const formatPercent = (value) =>
  value == null ? "0%" : `${Number(value).toFixed(1)}%`;

export const CompanyWiseBreakdownTable = ({
  check,
  getTableData,
  startDate,
  endDate,
}) => {
  const [sortBy, setSortBy] = useState("");
  const [sortType, setSortType] = useState("asc");
  const selectedCompany = useSelector(selectSelectedCompany);
  const user = useSelector(selectCurrentUser);
  const companyId = resolveActiveCompanyId(selectedCompany, user);

  const { data, isError, isLoading } = useGetAdminCompanyWiseBreakdownQuery(
    {
      sortBy,
      type: sortType,
      startDate,
      endDate,
      companyId,
    },
    {
      skip: !companyId,
      refetchOnMountOrArgChange: true,
    }
  );

  React.useEffect(() => {
    if (!data) return;
    getTableData && getTableData(data);
    // Parent passes an inline mapper; depend on data only to avoid re-running every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSorting = (sortKey) => {
    setSortBy(sortKey);
    setSortType(!sortType || sortType === "desc" ? "asc" : "desc");
  };

  if (isLoading) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  const allFieldsDeselected = Object.values(check).every((v) => !v);
  const visibleColumnCount = Object.values(check).filter(Boolean).length || 1;

  return (
    <div
      style={
        allFieldsDeselected
          ? { overflow: "hidden", maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <TableContainer
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 270px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflowX: allFieldsDeselected ? "hidden" : "auto",
          overflowY: allFieldsDeselected ? "hidden" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="company-wise breakdown table"
          sx={
            allFieldsDeselected
              ? { tableLayout: "fixed", width: "100%", minWidth: 0 }
              : { minWidth: 1267 }
          }
        >
          <TableHead>
            <TableRow
              sx={{ borderRadius: "20px 20px 0px 0px" }}
              className="tableUi__head"
            >
              {allFieldsDeselected ? (
                <TableCell
                  sx={{ borderRadius: "20px 20px 0px 0px" }}
                  align="center"
                >
                  Select fields to view data
                </TableCell>
              ) : (
                <>
                  {check.company && (
                    <TableCell
                      sx={{ borderRadius: "20px 0px 0px 0px" }}
                      align="center"
                      onClick={() => handleSorting("name")}
                    >
                      <div className="tableUi__head_sortable">
                        Company <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.totalEmployees && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("totalEmployees")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Employees <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.active && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("active")}
                    >
                      <div className="tableUi__head_sortable">
                        Active <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.inactive && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("inactive")}
                    >
                      <div className="tableUi__head_sortable">
                        Inactive <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.activePercent && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("activePercent")}
                    >
                      <div className="tableUi__head_sortable">
                        Active % <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.totalPtsEarned && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("totalPtsEarned")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Pts Earned <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.ptsRedeemed && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("ptsRedeemed")}
                    >
                      <div className="tableUi__head_sortable">
                        Pts Redeemed <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.balancePts && (
                    <TableCell
                      align="center"
                      onClick={() => handleSorting("balancePts")}
                    >
                      <div className="tableUi__head_sortable">
                        Balance Pts <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                  {check.totalAdmins && (
                    <TableCell
                      sx={{ borderRadius: "0px 20px 0px 0px" }}
                      align="center"
                      onClick={() => handleSorting("totalAdmins")}
                    >
                      <div className="tableUi__head_sortable">
                        Total Admins <SortIcon sortOrder={sortType} />
                      </div>
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {allFieldsDeselected ? (
              <TableRow>
                <TableCell align="center" sx={{ border: "none", py: 6 }}>
                  Select fields to view data
                </TableCell>
              </TableRow>
            ) : !data?.data?.length ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  No companies found.
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((row) => (
                <React.Fragment key={row._id ?? row.name}>
                  <TableRow className="tableUi___body">
                    {check.company && (
                      <TableCell align="center">
                        <ReportsTruncatedName value={row.name} />
                      </TableCell>
                    )}
                    {check.totalEmployees && (
                      <TableCell align="center">{row.totalEmployees}</TableCell>
                    )}
                    {check.active && (
                      <TableCell align="center">{row.active}</TableCell>
                    )}
                    {check.inactive && (
                      <TableCell align="center">{row.inactive}</TableCell>
                    )}
                    {check.activePercent && (
                      <TableCell align="center">
                        {formatPercent(row.activePercent)}
                      </TableCell>
                    )}
                    {check.totalPtsEarned && (
                      <TableCell align="center">{row.totalPtsEarned}</TableCell>
                    )}
                    {check.ptsRedeemed && (
                      <TableCell align="center">{row.ptsRedeemed}</TableCell>
                    )}
                    {check.balancePts && (
                      <TableCell align="center">{row.balancePts}</TableCell>
                    )}
                    {check.totalAdmins && (
                      <TableCell align="center">
                        {row.totalAdmins}
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
